/**
 * Video QA constants — shared between QAOptionsPanel and VideoQAReview.
 */

export const FINDING_TYPE_META = {
  duplicate_restart: { label: 'Dubbele herstart', color: '#ef4444', icon: '\u{1f501}', category: 'edit_hygiene' },
  false_start: { label: 'Valse start', color: '#f59e0b', icon: '\u23ed', category: 'edit_hygiene' },
  long_silence: { label: 'Lange stilte', color: '#6366f1', icon: '\u{1f507}', category: 'edit_hygiene' },
  off_camera_direction: { label: 'Productie-instructie', color: '#ef4444', icon: '\u{1f3ac}', category: 'edit_hygiene' },
  background_voice: { label: 'Achtergrondstem', color: '#f97316', icon: '\u{1f5e3}', category: 'edit_hygiene' },
  stumble_or_repeat: { label: 'Hapering', color: '#8b5cf6', icon: '\u{1f4ac}', category: 'edit_hygiene' },
  jump_cut: { label: 'Jump Cut', color: '#f97316', icon: '\u{1f4f7}', category: 'edit_hygiene' },
  weird_repetition: { label: 'Onnatuurlijke herhaling', color: '#8b5cf6', icon: '\u{1f504}', category: 'edit_hygiene' },
  missing_color_grade: { label: 'Kleurcorrectie ontbreekt', color: '#f97316', icon: '\u{1f3a8}', category: 'visual_quality' },
  color_inconsistency: { label: 'Kleursprong', color: '#eab308', icon: '\u{1f308}', category: 'visual_quality' },
  black_frames: { label: 'Zwarte frames', color: '#ef4444', icon: '\u2b1b', category: 'visual_quality' },
  exposure_fluctuation: { label: 'Belichtingsschommel', color: '#f59e0b', icon: '\u2600\ufe0f', category: 'visual_quality' },
  voice_level: { label: 'Stemniveau', color: '#6366f1', icon: '\u{1f399}\ufe0f', category: 'audio_quality' },
  audio_clipping: { label: 'Audio clipping', color: '#ef4444', icon: '\u{1f4a5}', category: 'audio_quality' },
  background_noise: { label: 'Achtergrondruis', color: '#f59e0b', icon: '\u{1f50a}', category: 'audio_quality' },
  loudness_consistency: { label: 'Volume consistentie', color: '#8b5cf6', icon: '\u{1f4c8}', category: 'audio_quality' },
  feedback_niet_verwerkt: { label: 'Feedback niet verwerkt', color: '#dc2626', icon: '\u{1f501}' },
  feedback_onzeker: { label: 'Feedback: onzeker of verwerkt', color: '#f59e0b', icon: '\u{1f501}' },
};

export const SEVERITY_META = {
  high: { label: 'Hoog', color: '#ef4444', bg: '#fef2f2' },
  medium: { label: 'Medium', color: '#f59e0b', bg: '#fffbeb' },
  low: { label: 'Laag', color: '#6b7280', bg: '#f9fafb' },
};

export const ACTION_LABELS = {
  cut_segment: 'Knippen',
  review_manually: 'Handmatig checken',
  replace_with_b_roll: 'B-roll eroverheen',
  show_slide_instead: 'Slide gebruiken',
  keep_but_check: 'Behouden, maar checken',
  not_an_issue: 'Geen probleem',
};

export const CATEGORY_META = {
  edit_hygiene: { label: 'Edit Hygiene', icon: '\u2702\ufe0f', color: '#6366f1', bg: '#eef2ff' },
  audio_quality: { label: 'Audiokwaliteit', icon: '\u{1f50a}', color: '#059669', bg: '#ecfdf5' },
  visual_quality: { label: 'Beeldkwaliteit', icon: '\u{1f3a8}', color: '#d97706', bg: '#fffbeb' },
  storytelling: { label: 'Storytelling', icon: '\u{1f4d6}', color: '#7c3aed', bg: '#f5f3ff' },
  snippets: { label: 'Snippet Suggesties', icon: '\u2702\ufe0f', color: '#0891b2', bg: '#ecfeff' },
  subtitle_quality: { label: 'Ondertitelkwaliteit', icon: '\u{1f4ac}', color: '#4f46e5', bg: '#eef2ff' },
  onscreen_text: { label: 'Tekst in beeld', icon: '\u{1f524}', color: '#be123c', bg: '#fff1f2' },
  feedback: { label: 'Feedbackcontrole', icon: '\u{1f501}', color: '#dc2626', bg: '#fef2f2' },
  improvement_tips: { label: 'Verbetervoorstellen', icon: '\u{1f4a1}', color: '#ea580c', bg: '#fff7ed' },
};

export const PRESETS = {
  rough_cut: {
    label: 'Rough Cut Check',
    description: 'Edit hygiene + basale audio checks',
    icon: '\u{1f3ac}',
    checks: {
      edit_hygiene: true,
      audio_quality: true,
      visual_quality: false,
      storytelling: false,
      snippets: false,
      subtitle_quality: false,
      onscreen_text: false,
      improvement_tips: true,
    },
  },
  final_delivery: {
    label: 'Final Delivery Check',
    description: 'Alle technische checks',
    icon: '\u2705',
    checks: {
      edit_hygiene: true,
      audio_quality: true,
      visual_quality: true,
      storytelling: false,
      snippets: false,
      subtitle_quality: true,
      onscreen_text: true,
      improvement_tips: true,
    },
  },
  storytelling_review: {
    label: 'Storytelling Review',
    description: 'Inhoudelijke review: hook, pacing, structuur',
    icon: '\u{1f4d6}',
    checks: {
      edit_hygiene: false,
      audio_quality: false,
      visual_quality: false,
      storytelling: true,
      snippets: false,
      subtitle_quality: false,
      onscreen_text: false,
      improvement_tips: true,
    },
  },
  social_snippet: {
    label: 'Social Snippet Mode',
    description: 'Beste fragmenten voor social media',
    icon: '\u{1f4f1}',
    checks: {
      edit_hygiene: false,
      audio_quality: false,
      visual_quality: false,
      storytelling: false,
      snippets: true,
      subtitle_quality: false,
      onscreen_text: false,
      improvement_tips: false,
    },
  },
  interview_mode: {
    label: 'Interview / Testimonial',
    description: 'Edit hygiene + storytelling + snippets',
    icon: '\u{1f3a4}',
    checks: {
      edit_hygiene: true,
      audio_quality: true,
      visual_quality: false,
      storytelling: true,
      snippets: true,
      subtitle_quality: false,
      onscreen_text: false,
      improvement_tips: true,
    },
  },
};

export const PRESET_ORDER = ['rough_cut', 'final_delivery', 'storytelling_review', 'social_snippet', 'interview_mode'];

export const STRICTNESS_OPTIONS = [
  { value: 'low', label: 'Soepel', description: 'Alleen duidelijke fouten' },
  { value: 'normal', label: 'Normaal', description: 'Standaard gevoeligheid' },
  { value: 'strict', label: 'Streng', description: 'Alles rapporteren' },
];

// Projecttype waarden uit Airtable (gesynchroniseerd vanuit Gripp)
export const PROJECT_TYPE_OPTIONS = [
  { value: 'Video', label: 'Video' },
  { value: 'Testimonial', label: 'Testimonial' },
  { value: 'Interview', label: 'Interview' },
  { value: 'Animatie', label: 'Animatie' },
  { value: 'Infographic', label: 'Infographic' },
  { value: 'Registratie', label: 'Registratie' },
  { value: 'Vlog', label: 'Vlog' },
  { value: 'Webinar', label: 'Webinar' },
  { value: 'Explainer', label: 'Explainer' },
  { value: 'Podcast', label: 'Podcast' },
];

// Backward compat alias
export const VIDEO_TYPE_OPTIONS = PROJECT_TYPE_OPTIONS;

export const PLATFORM_OPTIONS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'intern', label: 'Intern' },
];

export const TONE_OPTIONS = [
  { value: 'professioneel', label: 'Professioneel' },
  { value: 'casual', label: 'Casual' },
  { value: 'energiek', label: 'Energiek' },
  { value: 'emotioneel', label: 'Emotioneel' },
];
